/**
 * MobileKPIBar — horizontal scrollable KPI strip with fade edges.
 * Shows stability (with mini bar), treasury, GDP/mil/nuke/AI ranks, war status.
 */

import { useState } from 'react'

interface KPIData {
  stability: number
  deltaStability: number | null
  treasury: number
  deltaTreasury: number | null
  gdpRank: number
  deltaGdpRank: number | null
  milRank: number
  deltaMilRank: number | null
  nukeRank: number | null
  deltaNukeRank: number | null
  nukeLevel: number
  aiRank: number | null
  deltaAiRank: number | null
  aiLevel: number
  wars: string[]
}

interface Props {
  data: KPIData | null
}

function Arrow({ d, invert }: { d: number | null; invert?: boolean }) {
  if (d == null || Math.abs(d) < 0.01) return null
  const good = invert ? d < 0 : d > 0
  return (
    <span className={`text-[8px] font-bold ${good ? 'text-success' : 'text-danger'}`}>
      {d > 0 ? '\u25B2' : '\u25BC'}{Math.abs(d) < 10 ? Math.abs(d).toFixed(1) : Math.abs(d).toFixed(0)}
    </span>
  )
}

function RankArrow({ d }: { d: number | null }) {
  if (d == null || d === 0) return null
  return (
    <span className={`text-[8px] font-bold ${d > 0 ? 'text-success' : 'text-danger'}`}>
      {d > 0 ? '\u25B2' : '\u25BC'}{Math.abs(d)}
    </span>
  )
}

export function MobileKPIBar({ data }: Props) {
  const [hidden, setHidden] = useState(false)

  if (!data) return null

  const threshold = 4.0
  const stabColor = data.stability >= 7 ? 'bg-success' : data.stability > threshold ? 'bg-warning' : 'bg-danger'

  if (hidden) {
    return (
      <button onClick={() => setHidden(false)} className="bg-card border-b border-border px-3 py-1 w-full text-left">
        <span className="font-data text-[9px] text-text-secondary/50">\u25BC Show indicators</span>
      </button>
    )
  }

  const pills: { label: string; value: string; extra?: React.ReactNode }[] = [
    {
      label: 'Stab',
      value: data.stability.toFixed(1),
      extra: (
        <div className="flex items-center gap-1">
          <Arrow d={data.deltaStability} />
          <div className="w-8 h-1 bg-base rounded-full relative">
            <div className={`h-full rounded-full ${stabColor}`} style={{ width: `${Math.min(100, data.stability * 10)}%` }} />
            <div className="absolute top-[-1px] w-[1px] h-[3px] bg-danger rounded-sm" style={{ left: `${threshold * 10}%` }} />
          </div>
        </div>
      ),
    },
    { label: 'Trs', value: `ç${data.treasury}`, extra: <Arrow d={data.deltaTreasury} /> },
    { label: 'GDP', value: `#${data.gdpRank}`, extra: <RankArrow d={data.deltaGdpRank} /> },
    { label: 'Mil', value: `#${data.milRank}`, extra: <RankArrow d={data.deltaMilRank} /> },
  ]

  if (data.nukeLevel > 0 && data.nukeRank) {
    pills.push({ label: 'Nuke', value: `#${data.nukeRank} L${data.nukeLevel}`, extra: <RankArrow d={data.deltaNukeRank ?? null} /> })
  }
  if (data.aiLevel > 0 && data.aiRank) {
    pills.push({ label: 'AI', value: `#${data.aiRank} L${data.aiLevel}`, extra: <RankArrow d={data.deltaAiRank ?? null} /> })
  }
  if (data.wars.length > 0) {
    pills.push({ label: 'WAR', value: data.wars.slice(0, 2).join(', ') })
  }

  return (
    <div className="bg-card border-b border-border relative">
      <div className="flex items-center">
        <div className="flex-1 overflow-x-auto no-scrollbar">
          <div className="flex items-center gap-1 px-2 py-1.5 min-w-max">
            {pills.map((p, i) => (
              <div key={i} className={`flex items-center gap-1 px-2 py-0.5 rounded ${p.label === 'WAR' ? 'bg-danger/10' : 'bg-base'}`}>
                <span className="font-data text-[8px] font-bold uppercase text-text-secondary/60">{p.label}</span>
                <span className={`font-data text-[11px] font-semibold ${p.label === 'WAR' ? 'text-danger' : 'text-text-primary'}`}>{p.value}</span>
                {p.extra}
              </div>
            ))}
          </div>
        </div>
        <button onClick={() => setHidden(true)} className="px-1.5 py-1 text-text-secondary/40 hover:text-text-secondary text-[10px] flex-shrink-0">
          \u25B2
        </button>
      </div>
      {/* Fade edges for scroll affordance */}
      <div className="pointer-events-none absolute top-0 left-0 w-4 h-full bg-gradient-to-r from-card to-transparent" />
      <div className="pointer-events-none absolute top-0 right-6 w-4 h-full bg-gradient-to-l from-card to-transparent" />
    </div>
  )
}
